import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth, useTheme } from '../App';
import { toast } from 'sonner';
import { 
  User, 
  Shield, 
  Bell, 
  Palette,
  Globe,
  Building2,
  Key,
  Moon,
  Sun
} from 'lucide-react';

const Settings = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const roleInfo = {
    super_admin: { label: 'Super Admin', color: 'bg-red-500' },
    hr_admin: { label: 'HR Admin', color: 'bg-blue-500' },
    payroll_admin: { label: 'Payroll Admin', color: 'bg-green-500' },
    manager: { label: 'Manager', color: 'bg-amber-500' },
    employee: { label: 'Employee', color: 'bg-gray-500' },
  };

  const currentRole = roleInfo[user?.role] || { label: 'Unknown', color: 'bg-gray-500' };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your account and application preferences</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile">
            <User size={16} className="mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="appearance" data-testid="tab-appearance">
            <Palette size={16} className="mr-2" />
            Appearance
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Bell size={16} className="mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Shield size={16} className="mr-2" />
            Security
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Your personal details and role</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-6">
                <div className="h-20 w-20 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary-foreground">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold">
                    {user?.first_name} {user?.last_name}
                  </h3>
                  <p className="text-muted-foreground">{user?.email}</p>
                  <Badge className={`mt-2 ${currentRole.color}`}>
                    {currentRole.label}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t">
                <div>
                  <Label className="text-muted-foreground">First Name</Label>
                  <p className="font-medium">{user?.first_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Last Name</Label>
                  <p className="font-medium">{user?.last_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{user?.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Role</Label>
                  <p className="font-medium">{currentRole.label}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">User ID</Label>
                  <p className="font-medium font-mono text-sm">{user?.id}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Account Created</Label>
                  <p className="font-medium">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Customize the look and feel of the application</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {theme === 'dark' ? (
                    <Moon size={20} className="text-muted-foreground" />
                  ) : (
                    <Sun size={20} className="text-muted-foreground" />
                  )}
                  <div>
                    <Label>Dark Mode</Label>
                    <p className="text-sm text-muted-foreground">
                      Switch between light and dark themes
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={theme === 'dark'} 
                  onCheckedChange={toggleTheme}
                  data-testid="theme-switch"
                />
              </div>

              <div className="pt-6 border-t">
                <h4 className="font-medium mb-4">Theme Preview</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div 
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      theme === 'light' ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => theme === 'dark' && toggleTheme()}
                  >
                    <div className="w-full h-20 bg-white rounded border mb-2" />
                    <p className="text-sm font-medium text-center">Light</p>
                  </div>
                  <div 
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      theme === 'dark' ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => theme === 'light' && toggleTheme()}
                  >
                    <div className="w-full h-20 bg-slate-900 rounded border mb-2" />
                    <p className="text-sm font-medium text-center">Dark</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Configure how you receive notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive email updates for important activities
                  </p>
                </div>
                <Switch defaultChecked data-testid="email-notifications" />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Leave Requests</Label>
                  <p className="text-sm text-muted-foreground">
                    Notifications for leave approvals and rejections
                  </p>
                </div>
                <Switch defaultChecked data-testid="leave-notifications" />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Payroll Updates</Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified when payslips are generated
                  </p>
                </div>
                <Switch defaultChecked data-testid="payroll-notifications" />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Expense Claims</Label>
                  <p className="text-sm text-muted-foreground">
                    Updates on expense claim status
                  </p>
                </div>
                <Switch defaultChecked data-testid="expense-notifications" />
              </div>

              <div className="pt-4">
                <Button 
                  onClick={() => toast.success('Notification preferences saved')}
                  data-testid="save-notifications"
                >
                  Save Preferences
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>Manage your account security</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Key size={20} className="text-muted-foreground" />
                  <div>
                    <Label>Password</Label>
                    <p className="text-sm text-muted-foreground">
                      Last changed: Never
                    </p>
                  </div>
                </div>
                <Button variant="outline" data-testid="change-password-btn">
                  Change Password
                </Button>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Shield size={20} className="text-muted-foreground" />
                  <div>
                    <Label>Two-Factor Authentication</Label>
                    <p className="text-sm text-muted-foreground">
                      Add an extra layer of security
                    </p>
                  </div>
                </div>
                <Button variant="outline" data-testid="enable-2fa-btn">
                  Enable 2FA
                </Button>
              </div>

              <div className="pt-6 border-t">
                <h4 className="font-medium mb-4">Active Sessions</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <div>
                        <p className="text-sm font-medium">Current Session</p>
                        <p className="text-xs text-muted-foreground">
                          Browser · {new Date().toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary">Active</Badge>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button 
                  variant="destructive"
                  onClick={() => toast.info('This would log out all other sessions')}
                  data-testid="logout-all-btn"
                >
                  Log Out All Other Sessions
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
